import React, { useState } from 'react';
import styled from 'styled-components';
import {
  useFormatMessage,
  FormattedMessage,
} from '@artemis/integrations/contentful/utils';
import ResponsiveImage from '@artemis/components/ResponsiveImage';
import GoogleMapsAutocomplete from '@artemis/components/GoogleMapsAutocomplete';
import Loading from '@artemis/components/Loading';
import PropTypes from 'prop-types';

const Container = styled.div`
  padding-bottom: 30px;

  ${({ theme }) => theme.isTablet`
    max-width: 600px;
    padding: 0 53px 60px;
    margin: 0 auto;
  `};
`;

const ControlContainer = styled.div`
  > button {
    width: 100%;
    margin-top: 15px;
  }

  ${({ theme }) => theme.isTablet`
    display: flex;
    gap: 20px;
    > div:first-child {
      flex: 1;
    }
    > button {
      margin-top: 0;
    }
  `};
`;

const InputSearchContainer = styled.div`
  position: relative;

  > gmp-place-autocomplete {
    background: ${props => props.theme.palette.grey['100']};
    border: none;
    border-radius: 8px;
    width: 100%;
    box-sizing: border-box;

    &::part(input) {
      padding: 18px 0;
      border-radius: 8px;
    }
  }
`;

const Icon = styled(ResponsiveImage)`
  display: inline-block;
  height: 24px;
  position: absolute;
  width: 24px;
  left: 20px;
  top: 50%;
  transform: translateY(-50%);
`;

const LocationIcon = styled(Icon).attrs({
  id: 'brand.search.nearme.img',
})`
  left: calc(50vw - 85px);
  ${({ theme }) => theme.isTablet`
    left: 20px;
  `};
`;

const SearchButton = styled.button`
  background: ${({ theme }) => theme.palette.primary};
  border: none;
  border-radius: 8px;
  padding: 16px 16px 16px 48px;
  position: relative;
  flex: 1;
  color: ${({ theme }) => theme.palette.common.white};
  cursor: pointer;

  ${({ theme }) => theme.isTablet`
    max-width: 202px;
  `};
`;

const Error = styled.p`
  padding: 0.5em 0;
  color: ${props => props.theme.palette.dark.error};
`;

const getCurrentPosition = () =>
  new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      timeout: 15000,
      maximumAge: 900000,
    });
  });

const LocationControl = ({ onLocationChange }) => {
  const [error, setError] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(false);

  const doGeolocation = () => {
    setError(false);
    setLoading(true);

    getCurrentPosition()
      .then(({ coords: { latitude, longitude } }) =>
        onLocationChange({ lat: latitude, lng: longitude }),
      )
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  };

  const onPlacesChanged = data => {
    const locationData = data?.location;
    if (locationData) {
      onLocationChange({
        lat: locationData.lat(),
        lng: locationData.lng(),
      });
    }
  };

  return (
    <Container>
      <Loading
        isLoading={loading}
        style={{ position: 'fixed' }}
        data-testid="spinner"
      />
      <ControlContainer>
        <InputSearchContainer>
          <GoogleMapsAutocomplete
            showSearchIcon
            value={searchText}
            name="location-control-google-maps-search"
            placeholder={useFormatMessage({
              entry: 'brand.search.placeholder',
            })}
            onChange={e => setSearchText(e.target.value)}
            onPlacesChanged={onPlacesChanged}
          />
        </InputSearchContainer>
        <SearchButton
          type="button"
          onClick={doGeolocation}
          data-testid="searchbutton"
        >
          <LocationIcon />
          <FormattedMessage entry="brand.search.browserlocation" />
        </SearchButton>
      </ControlContainer>
      {error && <Error data-testid="searchbutton-error">{error}</Error>}
    </Container>
  );
};

LocationControl.propTypes = {
  onLocationChange: PropTypes.func,
};

export default LocationControl;
